import {
  IDatabasePropertyResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'

import {
  ICreatedAtPropertyTypeInterfaces,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/CreatedAtPropertyTypeStrategy/types/ICreatedAtPropertyTypeInterfaces'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {
  IUserResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IUserResponseInstance'
import {DatabaseViewType} from '@toolify/server/src/models/mongoose/DatabaseViewModel/enum/DatabaseViewType'
import {GroupSortModeType} from '@toolify/server/src/services/DatabasePropertyService/enum/GroupSortModeType'
import {
  dateValueToText,
  isDateRangeValue,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/DatePropertyTypeStrategy/dateValue'
import {
  IDatePropertyTypeFullValue,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/DatePropertyTypeStrategy/types/IDatePropertyTypeInterfaces'
import {COMMON_GROUP_SORT_LABEL_KEYS} from '@toolify/client/src/modules/databases/utils/groupSortModes'
import {
  entityWithDateGroupGranularityDescriptor,
} from '@toolify/client/src/modules/databases/utils/dateGroupGranularityDescriptor'

type EntityWithDateGroupValue = ICreatedAtPropertyTypeInterfaces['fullValue'] | IDatePropertyTypeFullValue | null

export function useDateWithEntityValueTypeStrategy() {

  const getSortableDate = (value: EntityWithDateGroupValue): string => {
    if(!value) {
      return ''
    }
    if(isDateRangeValue(value)) {
      return (value as IDatePropertyTypeFullValue).from?.date || ''
    }
    return (value as ICreatedAtPropertyTypeInterfaces['fullValue']).date || ''
  }

  const fullValueToText = (value: EntityWithDateGroupValue, _property: IDatabasePropertyResponseInstance) => {
    if(!value) {
      return ''
    }
    if(isDateRangeValue(value)) {
      return dateValueToText(value)
    }
    const entityValue = value as ICreatedAtPropertyTypeInterfaces['fullValue']
    let name = 'Unknown'
    if(entityValue?.entity?.entityType === EntityType.User) {
      name = (entityValue.entity.payload as IUserResponseInstance)?.nick
    }
    return entityValue?.date ? `${name} ${entityValue.date.toLocaleString()}` : ''
  }

  const compareItemValues = (
    valueA: EntityWithDateGroupValue,
    valueB: EntityWithDateGroupValue,
    sortDirection: '1' | '-1',
  ): number => {
    const dateA = getSortableDate(valueA)
    const dateB = getSortableDate(valueB)
    if(!dateA && !dateB) return 0
    if(!dateA) return 1
    if(!dateB) return -1
    const comparison = dateA.localeCompare(dateB)
    return sortDirection === '1' ? comparison : -comparison
  }

  const sortGroupItems = (a: {fullValue: EntityWithDateGroupValue}, b: {fullValue: EntityWithDateGroupValue}): number => {
    return compareItemValues(a.fullValue, b.fullValue, '1')
  }

  const canGroupByView = (viewType: DatabaseViewType, _property: IDatabasePropertyResponseInstance) =>
    viewType === DatabaseViewType.Table

  const groupGranularity = entityWithDateGroupGranularityDescriptor

  const groupSort = {
    availableModes: [GroupSortModeType.ValueAsc, GroupSortModeType.ValueDesc, GroupSortModeType.CountDesc, GroupSortModeType.CountAsc],
    defaultMode: GroupSortModeType.ValueAsc,
    getLabelKey: (mode: GroupSortModeType): string => {
      if(mode === GroupSortModeType.ValueAsc) return 'database.header.groupSort.dateAsc'
      if(mode === GroupSortModeType.ValueDesc) return 'database.header.groupSort.dateDesc'
      return COMMON_GROUP_SORT_LABEL_KEYS[mode]
    },
  }

  return {
    fullValueToText,
    compareItemValues,
    sortGroupItems,
    canGroupByView,
    groupGranularity,
    groupSort,
  }
}
